import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import type { BoxHelper, Object3D, Scene } from "three";
import { designTokensCss } from "../../core/ui-kit/designTokens";
import type { Engine } from "../../engine/Engine";
import {
  renderSceneDebugInspector,
  type SceneDebugInspectPanel,
} from "./SceneDebugInspector";
import { renderSceneDebugTree } from "./SceneDebugTree";
import {
  attachBoxHelper,
  collectExpandedIds,
  detachBoxHelper,
  filterNodes,
  findObject,
  resolveEngine,
  walkScene,
  type SceneDebugNode,
} from "./sceneDebugModel";

@customElement("scene-debug-menu")
export class SceneDebugMenu extends LitElement {
  static override styles = [
    designTokensCss,
    css`
      :host {
        display: block;
        position: relative;
        pointer-events: auto;
        font-family: var(--srd-font);
      }

      .sd-icon {
        width: var(--srd-tool-size);
        height: var(--srd-tool-size);
        padding: 0;
        border-radius: var(--srd-radius-sm);
        background: var(--srd-surface);
        backdrop-filter: var(--srd-blur);
        -webkit-backdrop-filter: var(--srd-blur);
        border: 1px solid var(--srd-border);
        box-shadow: var(--srd-shadow-md);
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        color: var(--srd-text-muted);
        transition: background var(--srd-transition), color var(--srd-transition);
      }

      .sd-icon:hover,
      .sd-icon[aria-expanded="true"] {
        background: var(--srd-surface-hover);
        color: var(--srd-text);
      }

      .sd-icon:focus-visible {
        outline: none;
        box-shadow: var(--srd-focus-ring);
      }

      .sd-drawer {
        position: fixed;
        top: 0;
        right: 0;
        width: min(400px, 100vw);
        height: 100vh;
        z-index: 10060;
        display: flex;
        flex-direction: column;
        background: var(--srd-surface-solid);
        border-left: 1px solid var(--srd-border);
        box-shadow: var(--srd-shadow-lg);
        color: var(--srd-text);
        transform: translateX(100%);
        pointer-events: none;
        transition: transform 180ms ease-out;
      }

      .sd-drawer.is-open {
        transform: translateX(0);
        pointer-events: auto;
      }

      .sd-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--srd-space-3);
        padding: 12px 14px;
        border-bottom: 1px solid var(--srd-border);
      }

      .sd-head h2 {
        margin: 0;
        font-size: 0.875rem;
        font-weight: 650;
        letter-spacing: -0.02em;
      }

      .sd-section {
        display: flex;
        flex-direction: column;
        min-height: 0;
      }

      .sd-hier {
        flex: 0 0 32%;
        min-height: 128px;
        border-bottom: 1px solid var(--srd-border);
      }

      .sd-section-label {
        margin: 0;
        padding: 10px 14px 6px;
        font-size: 0.625rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--srd-text-muted);
      }

      .sd-close {
        width: 32px;
        height: 32px;
        border: 0;
        border-radius: var(--srd-radius-sm);
        background: transparent;
        color: var(--srd-text-muted);
        cursor: pointer;
        font-size: 18px;
      }

      .sd-close:hover {
        background: var(--srd-bg-muted);
        color: var(--srd-text);
      }

      .sd-search {
        margin: 0 14px 8px;
      }

      .sd-search input,
      .sd-field input,
      .sd-field select,
      .sd-field output,
      .sd-url,
      .sd-xyz input {
        width: 100%;
        box-sizing: border-box;
        border: 1px solid var(--srd-border-strong);
        border-radius: var(--srd-radius-sm);
        background: var(--srd-bg);
        color: var(--srd-text);
        font: inherit;
        font-size: 0.75rem;
        padding: 6px 8px;
      }

      .sd-search input:focus,
      .sd-field input:focus,
      .sd-field select:focus,
      .sd-url:focus,
      .sd-xyz input:focus {
        outline: none;
        box-shadow: var(--srd-focus-ring);
      }

      .sd-tree-wrap {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        padding: 0 0 8px;
      }

      .sd-inspect {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      .sd-tabs {
        display: flex;
        margin: 10px 14px 0;
        border: 1px solid var(--srd-border-strong);
        border-radius: var(--srd-radius-sm);
        overflow: hidden;
        background: var(--srd-bg);
        flex: 0 0 auto;
      }

      .sd-tab {
        flex: 1;
        appearance: none;
        margin: 0;
        border: 0;
        background: transparent;
        color: var(--srd-text-muted);
        font: inherit;
        font-size: 0.75rem;
        font-weight: 650;
        padding: 7px 8px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
      }

      .sd-tab + .sd-tab {
        border-left: 1px solid var(--srd-border-strong);
      }

      .sd-tab:hover {
        color: var(--srd-text);
        background: var(--srd-bg-muted);
      }

      .sd-tab.is-active {
        color: var(--srd-button-text);
        background: var(--srd-button);
      }

      .sd-tab:focus-visible {
        outline: none;
        box-shadow: inset var(--srd-focus-ring);
      }

      .sd-tab-count {
        min-width: 16px;
        height: 16px;
        padding: 0 5px;
        border-radius: 8px;
        font-size: 0.625rem;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--srd-bg-muted);
        color: var(--srd-text-muted);
      }

      .sd-tab.is-active .sd-tab-count {
        background: rgba(255, 255, 255, 0.18);
        color: inherit;
      }

      .sd-panel {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        padding: 12px 14px 20px;
      }

      .sd-tree,
      .sd-tree ul {
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .sd-tree-row {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 4px;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        font-size: 0.75rem;
        min-height: 24px;
        cursor: pointer;
        text-align: left;
      }

      .sd-tree-row:hover {
        background: var(--srd-bg-muted);
      }

      .sd-tree-row.is-selected {
        background: var(--srd-accent-soft);
      }

      .sd-tree-toggle {
        width: 14px;
        height: 14px;
        flex: 0 0 14px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--srd-text-muted);
      }

      .sd-tree-toggle svg {
        display: block;
        width: 8px;
        height: 8px;
        transition: transform 120ms ease;
      }

      .sd-tree-toggle.is-open svg {
        transform: rotate(90deg);
      }

      .sd-tree-toggle.is-leaf {
        visibility: hidden;
      }

      .sd-tree-badge {
        flex: 0 0 auto;
        font-size: 0.5625rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--srd-text-muted);
      }

      .sd-tree-name {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .sd-tree-meta {
        color: var(--srd-text-muted);
        font-variant-numeric: tabular-nums;
        font-size: 0.6875rem;
      }

      .sd-block {
        margin: 0 0 var(--srd-space-4);
      }

      .sd-block h3,
      .sd-block h4 {
        margin: 0 0 var(--srd-space-2);
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--srd-text-muted);
      }

      .sd-readonly,
      .sd-empty {
        margin: 0 0 var(--srd-space-3);
        font-size: 0.75rem;
        color: var(--srd-text-muted);
        word-break: break-all;
      }

      .sd-inspect > .sd-empty {
        margin: 0;
        padding: 16px 14px;
      }

      .sd-fields {
        display: grid;
        gap: 8px;
        margin-bottom: var(--srd-space-3);
      }

      .sd-field {
        display: grid;
        grid-template-columns: 88px 1fr;
        gap: 8px;
        align-items: center;
        font-size: 0.75rem;
      }

      .sd-field-inline {
        grid-template-columns: 88px auto;
        justify-content: start;
      }

      .sd-field-vec {
        align-items: start;
        margin-bottom: 10px;
      }

      .sd-xyz {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
      }

      .sd-axis {
        display: grid;
        gap: 3px;
      }

      .sd-axis > span {
        font-size: 0.5625rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--srd-text-muted);
      }

      .sd-check,
      .sd-check-row,
      .sd-file {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 0.75rem;
      }

      .sd-maps {
        display: grid;
        gap: var(--srd-space-3);
      }

      .sd-map {
        border: 1px solid var(--srd-border);
        border-radius: var(--srd-radius-md);
        padding: var(--srd-space-3);
        background: var(--srd-bg);
      }

      .sd-map.is-filled {
        border-color: rgba(34, 197, 94, 0.28);
      }

      .sd-map-head {
        display: flex;
        gap: var(--srd-space-3);
        align-items: center;
        margin-bottom: var(--srd-space-2);
      }

      .sd-map-preview {
        width: 40px;
        height: 40px;
        border-radius: 6px;
        overflow: hidden;
        background: #111;
        color: #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.625rem;
        flex: 0 0 40px;
      }

      .sd-map-preview img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .sd-map-titles {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .sd-map-titles strong {
        font-size: 0.8125rem;
      }

      .sd-map-titles span {
        font-size: 0.6875rem;
        color: var(--srd-text-muted);
      }

      .sd-map-xform {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 6px;
        margin-bottom: var(--srd-space-2);
        font-size: 0.6875rem;
      }

      .sd-map-actions {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 6px;
        align-items: center;
      }

      .sd-map-export {
        display: flex;
        gap: 6px;
        margin-top: 6px;
      }

      .sd-file {
        position: relative;
        overflow: hidden;
        padding: 6px 8px;
        border: 1px solid var(--srd-border-strong);
        border-radius: var(--srd-radius-sm);
        cursor: pointer;
        background: var(--srd-surface-solid);
      }

      .sd-file input {
        position: absolute;
        inset: 0;
        opacity: 0;
        cursor: pointer;
      }

      .sd-btn {
        border: 0;
        border-radius: var(--srd-radius-sm);
        background: var(--srd-button);
        color: var(--srd-button-text);
        font: inherit;
        font-size: 0.6875rem;
        font-weight: 650;
        padding: 6px 8px;
        cursor: pointer;
      }

      .sd-btn:hover {
        background: var(--srd-button-hover);
      }

      .sd-btn-ghost {
        background: transparent;
        border: 1px solid var(--srd-border-strong);
        color: var(--srd-text);
      }

      .sd-btn-ghost:disabled {
        opacity: 0.4;
        cursor: default;
      }

      @media (prefers-reduced-motion: reduce) {
        .sd-drawer,
        .sd-tree-toggle svg {
          transition: none;
        }
      }
    `,
  ];

  @state() private _open = false;
  @state() private _query = "";
  @state() private _selectedId: string | null = null;
  @state() private _nodes: SceneDebugNode[] = [];
  @state() private _urlDrafts: Record<string, string> = {};
  @state() private _inspectPanel: SceneDebugInspectPanel = "transform";

  private _expanded = new Set<string>();
  private _engine?: Engine;
  private _helper?: BoxHelper;
  private _poll: number | null = null;

  private readonly _onSceneMutated = () => {
    this._refreshTree();
    this._syncHelper();
  };

  private readonly _onKey = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this._open) this._setOpen(false);
  };

  disconnectedCallback(): void {
    this._unbindEngine();
    this._setOpen(false);
    super.disconnectedCallback();
  }

  private _setOpen(open: boolean): void {
    this._open = open;
    if (open) {
      window.addEventListener("keydown", this._onKey);
      this._ensureEngine();
      this._startPoll();
      this._refreshTree(true);
    } else {
      window.removeEventListener("keydown", this._onKey);
      this._stopPoll();
      detachBoxHelper(this._helper);
      this._helper = undefined;
    }
  }

  private _startPoll(): void {
    if (this._poll != null) return;
    this._poll = window.setInterval(() => this._ensureEngine(), 400);
  }

  private _stopPoll(): void {
    if (this._poll == null) return;
    window.clearInterval(this._poll);
    this._poll = null;
  }

  private _ensureEngine(): void {
    const engine = resolveEngine();
    if (engine === this._engine) return;
    this._unbindEngine();
    this._engine = engine;
    engine?.addEventListener("scene-mutated", this._onSceneMutated);
    this._refreshTree(true);
  }

  private _unbindEngine(): void {
    this._engine?.removeEventListener("scene-mutated", this._onSceneMutated);
    this._engine = undefined;
  }

  private get _scene(): Scene | undefined {
    return this._engine?.activeScene;
  }

  private _refreshTree(seedExpand = false): void {
    this._nodes = walkScene(this._scene);
    if (seedExpand && this._expanded.size === 0) {
      this._expanded = collectExpandedIds(this._nodes);
    }
    if (this._selectedId && !findObject(this._scene, this._selectedId)) {
      this._selectedId = null;
      detachBoxHelper(this._helper);
      this._helper = undefined;
    }
    this.requestUpdate();
  }

  private _syncHelper(): void {
    const object = this._selectedObject;
    if (!object) return;
    this._helper?.update();
  }

  private get _selectedObject(): Object3D | undefined {
    if (!this._selectedId) return undefined;
    return findObject(this._scene, this._selectedId);
  }

  private _select(id: string): void {
    this._selectedId = id;
    this._expanded.add(id);
    const object = findObject(this._scene, id);
    if (object) {
      this._helper = attachBoxHelper(this._scene, object, this._helper);
    }
  }

  private _toggle(id: string): void {
    if (this._expanded.has(id)) this._expanded.delete(id);
    else this._expanded.add(id);
    this.requestUpdate();
  }

  protected override render() {
    const visibleNodes = filterNodes(this._nodes, this._query);

    return html`
      <button
        type="button"
        class="sd-icon"
        aria-label="Scene debug"
        aria-expanded=${this._open ? "true" : "false"}
        @click=${() => this._setOpen(!this._open)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 3 4 7.5 12 12l8-4.5L12 3Z" />
          <path d="M4 12 12 16.5 20 12" />
          <path d="M4 16.5 12 21l8-4.5" />
        </svg>
      </button>

      <aside class="sd-drawer ${this._open ? "is-open" : ""}" aria-hidden=${this._open ? "false" : "true"}>
        <div class="sd-head">
          <h2>Scene debug</h2>
          <button type="button" class="sd-close" aria-label="Close" @click=${() => this._setOpen(false)}>
            ×
          </button>
        </div>
        <section class="sd-section sd-hier">
          <p class="sd-section-label">Hierarchy</p>
          <div class="sd-search">
            <input
              type="search"
              placeholder="Filter objects"
              .value=${this._query}
              @input=${(event: Event) => {
                this._query = (event.currentTarget as HTMLInputElement).value;
              }}
            />
          </div>
          <div class="sd-tree-wrap">
            ${this._open
              ? this._nodes.length
                ? renderSceneDebugTree({
                    nodes: visibleNodes,
                    selectedId: this._selectedId,
                    expanded: this._expanded,
                    query: this._query,
                    onToggle: (id) => this._toggle(id),
                    onSelect: (id) => this._select(id),
                  })
                : html`<p class="sd-empty" style="padding:8px 14px">No active scene.</p>`
              : nothing}
          </div>
        </section>
        <section class="sd-section sd-inspect">
          ${this._open
            ? renderSceneDebugInspector({
                object: this._selectedObject,
                scene: this._scene,
                engine: this._engine,
                panel: this._inspectPanel,
                onPanel: (panel) => {
                  this._inspectPanel = panel;
                },
                urlDrafts: this._urlDrafts,
                onUrlDraft: (key, value) => {
                  this._urlDrafts = { ...this._urlDrafts, [key]: value };
                },
                onChanged: () => this.requestUpdate(),
              })
            : nothing}
        </section>
      </aside>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "scene-debug-menu": SceneDebugMenu;
  }
}
